import { useEffect, useRef, useState, type RefObject } from 'react'
import './audio-waveform.css'
import { createSpectrumRenderer } from './audioSpectrum'

export type AudioVisualizationMode = 'waveform' | 'spectrum'

/** Shared live analysis for both renderers; switching views preserves the audio graph. */
export function AudioVisualization({
  videos,
  active,
  enabled,
  name,
  amplitudeEnvelope = true,
  visualization = 'waveform',
}: {
  videos: RefObject<(HTMLVideoElement | null)[]>
  active: number
  enabled: boolean
  name: string
  amplitudeEnvelope?: boolean
  visualization?: AudioVisualizationMode
}) {
  const canvas = useRef<HTMLCanvasElement>(null)
  const current = useRef({ active, enabled, amplitudeEnvelope, visualization })
  current.current = { active, enabled, amplitudeEnvelope, visualization }
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let context: AudioContext | undefined
    const nodes = new Map<HTMLMediaElement, AnalyserNode>()
    const sources: MediaElementAudioSourceNode[] = []
    const samples = new Uint8Array(2048)
    const frequencies = new Uint8Array(samples.length / 2)
    const spectrum = createSpectrumRenderer()
    let lastView = visualization
    let frame = 0
    let previous = 0
    let peak = 0
    let peakAt = 0
    let peakSource = ''

    const scale = (amplitude: number) =>
      (1.3 * amplitude) / (1 + 0.3 * Math.abs(amplitude))

    function connect() {
      const media = videos.current[current.current.active]
      if (!current.current.enabled || !media) return
      try {
        context ??= new AudioContext()
        if (!nodes.has(media)) {
          const analyser = context.createAnalyser()
          analyser.fftSize = samples.length
          const source = context.createMediaElementSource(media)
          // Keep the audible path independent of the visualization.
          source.connect(context.destination)
          source.connect(analyser)
          sources.push(source)
          nodes.set(media, analyser)
        }
        void context.resume().catch(() => setUnavailable(true))
      } catch {
        setUnavailable(true)
      }
    }
    function resume() {
      // Once routed through Web Audio, video playback also needs this context.
      if (context) void context.resume().catch(() => {})
      connect()
    }
    const media = videos.current.filter((v) => v !== null)
    media.forEach((video) => video.addEventListener('play', connect))
    window.addEventListener('pointerdown', resume)
    window.addEventListener('pointerup', resume)
    window.addEventListener('keydown', resume)

    function draw(now: number) {
      frame = requestAnimationFrame(draw)
      const element = canvas.current
      if (!current.current.enabled || !element || document.hidden || now - previous < 33)
        return
      previous = now
      const painter = element.getContext('2d')
      if (!painter) return
      const width = element.clientWidth
      const height = element.clientHeight
      const ratio = window.devicePixelRatio || 1
      if (
        element.width !== Math.round(width * ratio) ||
        element.height !== Math.round(height * ratio)
      ) {
        element.width = Math.round(width * ratio)
        element.height = Math.round(height * ratio)
      }
      painter.setTransform(ratio, 0, 0, ratio, 0, 0)
      painter.clearRect(0, 0, width, height)
      const video = videos.current[current.current.active]
      const analyser = video && nodes.get(video)
      samples.fill(128)
      const live = analyser && !video.paused && !video.seeking && video.readyState >= 2
      const source = `${current.current.active}:${video?.currentSrc ?? ''}`
      if (!live || source !== peakSource || lastView !== current.current.visualization) {
        peak = 0
        peakAt = now
        peakSource = source
        spectrum.reset()
      }
      lastView = current.current.visualization
      if (current.current.visualization === 'spectrum') {
        frequencies.fill(0)
        if (live) analyser.getByteFrequencyData(frequencies)
        spectrum.draw(painter, element, frequencies, context?.sampleRate ?? 48000, now)
        return
      }
      if (live) analyser.getByteTimeDomainData(samples)
      const retained = peak * Math.max(0, 1 - (now - peakAt) / 1000)
      let amplitudePeak = 0
      for (const sample of samples)
        amplitudePeak = Math.max(amplitudePeak, Math.abs((sample - 128) / 128))
      if (amplitudePeak >= retained) {
        peak = amplitudePeak
        peakAt = now
      }
      const fade = Math.max(0, 1 - (now - peakAt) / 1000)
      const envelope = scale(peak * fade) * height * 0.45
      const accent =
        getComputedStyle(element).getPropertyValue('--vod-accent').trim() || '#f2cb55'
      if (current.current.amplitudeEnvelope && envelope > 1) {
        painter.fillStyle = accent
        painter.globalAlpha = 0.045 * fade
        painter.fillRect(0, height / 2 - envelope, width, envelope * 2)
        painter.strokeStyle = accent
        painter.globalAlpha = 0.28 * fade
        painter.lineWidth = 1
        // painter.setLineDash([4, 6])
        painter.beginPath()
        for (const y of [height / 2 - envelope, height / 2 + envelope]) {
          painter.moveTo(0, y)
          painter.lineTo(width, y)
        }
        painter.stroke()
        painter.setLineDash([])
      }
      painter.globalAlpha = 1
      painter.strokeStyle = accent
      painter.lineWidth = 2
      painter.beginPath()
      for (let i = 0; i < samples.length; i++) {
        const x = (i / (samples.length - 1)) * width
        const amplitude = (samples[i] - 128) / 128
        // ~30% more height for quiet audio, tapering to unity at full scale.
        // This display-only curve preserves headroom without clipping loud peaks.
        const scaled = scale(amplitude)
        const y = height / 2 + scaled * height * 0.45
        if (i === 0) painter.moveTo(x, y)
        else painter.lineTo(x, y)
      }
      painter.stroke()
    }
    frame = requestAnimationFrame(draw)
    return () => {
      cancelAnimationFrame(frame)
      window.removeEventListener('pointerdown', resume)
      window.removeEventListener('pointerup', resume)
      window.removeEventListener('keydown', resume)
      media.forEach((video) => video.removeEventListener('play', connect))
      sources.forEach((source) => source.disconnect())
      nodes.forEach((node) => node.disconnect())
      if (context) void context.close()
    }
  }, [videos])

  return enabled ? (
    <div
      className={`audio-waveform ${visualization === 'spectrum' ? 'audio-spectrum' : ''}`}
      role="img"
      aria-label={`${name} live audio ${visualization}`}
    >
      <div className="audio-waveform-label">
        <strong>{name}</strong>
        {unavailable && <span>Audio visualization unavailable</span>}
      </div>
      <canvas ref={canvas} aria-hidden="true" />
    </div>
  ) : null
}
